import {Checkbox, FormControlLabel, Typography, css, useTheme} from '@mui/material';
import {
  GetStylesFunction,
  IconHeaderWithSearch,
  LightSplitLayout,
  useLocalStorage,
  LightComponentBox,
  DlMuiToggleFilters,
  useDefaultScroll,
  getScrollOffset,
} from '@wallaby-ui/core';
import {sortBy} from 'lodash';
import React, {useCallback, useMemo, useRef} from 'react';

import {Formatters, useFormatters} from './light/LightFormatter';
import {components} from '../_generated/haidi_schema';

type Document = components['schemas']['DocumentViewDto'];
type Snippet = Document['documentSnippets'][0];
type SnippetWithSelectedType = Snippet & {
  selectedEventType: Snippet['eventTypes'][0];
};

export function getDocumentTimestamp(document: Document | undefined): string | undefined {
  return document?.documentProperties?.effectiveAtTimestamp;
}
export type DocumentColors = {text: string; background: string; highlight: string};

function selectEventType(
  disabledEventTypes: string[],
  snippetPriorityTable: string[],
  eventTypes: string[] | undefined
): {
  priority: number;
  value: string | undefined;
} {
  const filteredEventTypes = (eventTypes ?? []).filter((v) => !disabledEventTypes.includes(v));
  let priority = Infinity;
  let value: string | undefined = filteredEventTypes[0];
  filteredEventTypes.forEach((eventType) => {
    const newPriority = snippetPriorityTable.findIndex((w) => eventType.match(new RegExp(`^${w}$`)) != null);
    if (newPriority < priority) {
      priority = newPriority;
      value = eventType;
    }
  });
  return {priority, value};
}

function getNextSnippet(
  i: number,
  prevSnippetSearchStart: number,
  sortedSnippets: SnippetWithSelectedType[],
  disabledEventTypes: string[],
  snippetPriorityTable: string[]
): {
  snippet: SnippetWithSelectedType | undefined;
  snippetSearchStart: number;
} {
  let snippetSearchStart = prevSnippetSearchStart;
  const matchingSnippets: SnippetWithSelectedType[] = [];
  for (let j = snippetSearchStart; j < sortedSnippets.length; j++) {
    const snippetOption = sortedSnippets[j];
    if (snippetOption.endPosition < i && j === snippetSearchStart) {
      snippetSearchStart += 1;
    }
    if (snippetOption.startPosition <= i && snippetOption.endPosition > i) {
      matchingSnippets.push(snippetOption);
    }
    if (snippetOption.startPosition > i) {
      break;
    }
  }
  let snippet: SnippetWithSelectedType | undefined;
  if (matchingSnippets.length) {
    [snippet] = sortBy(matchingSnippets, (v) => selectEventType(disabledEventTypes, snippetPriorityTable, v.eventTypes).priority);
  }
  return {snippet, snippetSearchStart};
}

type UseDocumentInfoProps = {
  document: Document | undefined;
  disabledEventTypes: string[];
  formatters: Formatters;
  getDocumentColors: (type: string) => DocumentColors;
  snippetPriorityTable: string[];
  documentSnippetOnClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
};
export function useDocumentInfo(props: UseDocumentInfoProps) {
  const {document, disabledEventTypes, formatters, getDocumentColors, snippetPriorityTable, documentSnippetOnClick} = props;
  const {tEnum} = formatters;
  const sortedSnippets = useMemo(
    () =>
      sortBy(
        (document?.documentSnippets ?? [])
          .map(
            (v) =>
              ({
                ...v,
                selectedEventType: selectEventType(disabledEventTypes, snippetPriorityTable, v.eventTypes).value,
              } as SnippetWithSelectedType)
          )
          .filter((v) => v.selectedEventType),
        ['startPosition', 'endPosition']
      ),
    [document?.documentSnippets, disabledEventTypes, snippetPriorityTable]
  );
  const formattedDocumentSnippets = useMemo(() => {
    const rawText = document?.documentContent ?? '';
    let prevI = 0;
    let {snippet: prevSnippet, snippetSearchStart: prevSnippetSearchStart} = getNextSnippet(
      0,
      0,
      sortedSnippets,
      disabledEventTypes,
      snippetPriorityTable
    );
    const accFormattedSnippets: JSX.Element[] = [];
    const addSnippet = (i: number) => {
      if (prevSnippet) {
        const colors = getDocumentColors(prevSnippet.selectedEventType);
        accFormattedSnippets.push(
          <span
            key={prevI}
            style={{
              background: colors.background,
              color: colors.text,
              cursor: documentSnippetOnClick ? 'pointer' : undefined,
            }}
            data-name='document-snippet'
            data-id={prevSnippet.id}
            onClick={documentSnippetOnClick}
          >
            {rawText.slice(prevI, i)}
          </span>
        );
      } else {
        accFormattedSnippets.push(<span key={prevI}>{rawText.slice(prevI, i)}</span>);
      }
      prevI = i;
    };
    for (let i = 1; i <= rawText.length; i++) {
      const {snippet, snippetSearchStart} = getNextSnippet(
        i,
        prevSnippetSearchStart,
        sortedSnippets,
        disabledEventTypes,
        snippetPriorityTable
      );
      if (snippet !== prevSnippet) {
        addSnippet(i);
      }
      prevSnippet = snippet;
      prevSnippetSearchStart = snippetSearchStart;
    }
    if (prevI < rawText.length) {
      addSnippet(rawText.length);
    }
    return accFormattedSnippets;
  }, [document?.documentContent, sortedSnippets, disabledEventTypes, getDocumentColors, snippetPriorityTable, documentSnippetOnClick]);
  const extractedInfos = useMemo(() => {
    const accInfos: any[] = [];
    for (let i = 0; i < sortedSnippets.length; i++) {
      const snippet = sortedSnippets[i];
      const eventTypes = snippet?.eventTypes ?? [];
      for (let j = 0; j < eventTypes.length; j++) {
        const eventType = eventTypes[j];
        if (!disabledEventTypes.includes(eventType))
          accInfos.push({
            ...snippet,
            selectedEventType: eventType,
          });
      }
    }
    return accInfos;
  }, [sortedSnippets, disabledEventTypes]);
  const legendTypes = useMemo(() => {
    const snippets = document?.documentSnippets ?? [];
    let accEventTypes: string[] = [];
    for (let i = 0; i < snippets.length; i++) {
      accEventTypes = [...accEventTypes, ...snippets[i].eventTypes];
    }
    return sortBy(Array.from(new Set(accEventTypes)), (v) => tEnum('HaidiDocumentEventTypeEnum', v));
  }, [document?.documentSnippets, tEnum]);
  return {sortedSnippets, formattedDocumentSnippets, extractedInfos, legendTypes};
}

type Props = {
  storageId: string;
  document: Document | undefined;
  getDocumentColors: (eventType: string) => DocumentColors;
  snippetPriorityTable: string[];
  iconName?: string;
  heightOffset?: number;
};
export const LightDocument: React.FC<Props> = (props) => {
  const {document, iconName = 'haidiDocumentIconMultiColor', storageId, heightOffset = 0, getDocumentColors, snippetPriorityTable} = props;
  const formatters = useFormatters();
  const {t, tEnum, formatDateTime} = formatters;
  const documentScrollRef = useRef(null as HTMLDivElement | null);
  const infoScrollRef = useRef(null as HTMLDivElement | null);
  const [documentScroll, setDocumentScroll] = useLocalStorage(`${storageId}.documentScroll`, 0);
  const [infoScroll, setInfoScroll] = useLocalStorage(`${storageId}.infoScroll`, 0);
  useDefaultScroll(documentScrollRef, {
    to: documentScroll,
    dependOn: documentScroll,
  });
  useDefaultScroll(infoScrollRef, {to: infoScroll, dependOn: infoScroll});

  const [disabledEventTypes, _, setDisabledEventTypes] = useLocalStorage(`${storageId}.disabledEventTypes`, [] as string[]);
  const toggleDisabledEventType = useCallback(
    (v: string) => {
      const i = disabledEventTypes.indexOf(v);
      if (i === -1) {
        setDisabledEventTypes([...disabledEventTypes, v]);
      } else {
        const newDisabledEventTypes = [...disabledEventTypes];
        newDisabledEventTypes.splice(i, 1);
        setDisabledEventTypes(newDisabledEventTypes);
      }
    },
    [disabledEventTypes, setDisabledEventTypes]
  );
  const documentSnippetOnClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (!infoScrollRef.current) return;
      const infoId = (event.target as HTMLElement).getAttribute('data-id');
      const info = infoScrollRef.current.querySelector<HTMLElement>(`[data-id="${infoId}"]`);
      const scrollY = infoScrollRef.current.scrollTop;
      const scrollOffset = getScrollOffset(infoScrollRef.current, info);
      infoScrollRef.current.scrollTo(0, scrollY + scrollOffset);
      setInfoScroll(scrollY + scrollOffset);
    },
    [setInfoScroll]
  );
  const extractedInfoOnClick = useCallback(
    (snippetId: number) => (_: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (!documentScrollRef.current) return;
      const snippet = documentScrollRef.current.querySelector<HTMLElement>(`[data-id="${snippetId}"]`);
      const scrollY = documentScrollRef.current.scrollTop;
      const scrollOffset = getScrollOffset(documentScrollRef.current, snippet);
      documentScrollRef.current.scrollTo(0, scrollY + scrollOffset);
      setDocumentScroll(scrollY + scrollOffset);
    },
    [setDocumentScroll]
  );
  const {formattedDocumentSnippets, extractedInfos, legendTypes} = useDocumentInfo({
    document,
    disabledEventTypes,
    formatters,
    getDocumentColors,
    snippetPriorityTable,
    documentSnippetOnClick,
  });

  const [isExtractedInfoHidden, _2, setIsExtractedInfoHidden] = useLocalStorage(`${storageId}.isExtractedInfoHidden`, false);
  const toggleExtractedInfoButton = useMemo(
    () => (
      <DlMuiToggleFilters
        title={isExtractedInfoHidden ? 'document.show.extractedInfo' : 'document.hide.extractedInfo'}
        onClick={() => setIsExtractedInfoHidden(!isExtractedInfoHidden)}
        areFiltersOpen={!isExtractedInfoHidden}
        iconName='haidiShowHideIconMultiColor'
        useFixedViewBox='0 0 16 14'
      />
      /* TODO: when we have colors for haidi:
      <LightIconButton
        iconName='haidiShowHideIconMultiColor'
        useFixedViewBox='0 0 16 14'
        style={{width: 16, height: 14}}
        title={isExtractedInfoHidden ? 'document.show.extractedInfo' : 'document.hide.extractedInfo'}
        onClick={() => setIsExtractedInfoHidden(!isExtractedInfoHidden)}
        selected={!isExtractedInfoHidden}
      />
      */
    ),
    [isExtractedInfoHidden, setIsExtractedInfoHidden]
  );

  const theme = useTheme();
  const muiStyles = getStyles({theme});
  return (
    <LightSplitLayout
      data-name='document'
      initSizes={[836, 410]}
      minSizes={[100, 150]}
      storageId={`${storageId}.LightSplitLayout`}
      hidden={[false, isExtractedInfoHidden]}
      style={{height: `calc(100% + ${heightOffset}px)`}}
    >
      <LightComponentBox data-name='document-column' hasScrollbar css={muiStyles.documentColumn}>
        <IconHeaderWithSearch
          label={
            document?.documentProperties?.documentName
              ? `${document?.documentProperties?.documentName}, ${formatDateTime(getDocumentTimestamp(document) ?? '')}`
              : '...'
          }
          iconName={iconName}
        >
          {isExtractedInfoHidden && toggleExtractedInfoButton}
        </IconHeaderWithSearch>
        <div
          data-name='document'
          css={muiStyles.document}
          ref={documentScrollRef}
          onScroll={(event) => {
            setDocumentScroll((event.target as HTMLDivElement).scrollTop);
          }}
        >
          {formattedDocumentSnippets}
        </div>
      </LightComponentBox>
      <LightComponentBox data-name='info-legend-column' css={muiStyles.infoLegendColumn}>
        <IconHeaderWithSearch label={t('document.concepts.title')}>
          {!isExtractedInfoHidden && toggleExtractedInfoButton}
        </IconHeaderWithSearch>
        <div
          data-name='extracted-info-column'
          css={muiStyles.extractedInfoColumn}
          ref={infoScrollRef}
          onScroll={(event) => {
            setInfoScroll((event.target as HTMLDivElement).scrollTop);
          }}
        >
          {extractedInfos.map((extractedInfo) => {
            const colors = getDocumentColors(extractedInfo.selectedEventType);
            const headerText = (document?.documentContent ?? '').slice(extractedInfo.startPosition, extractedInfo.endPosition);
            const EMPTY = '-';
            const atcGroups = (extractedInfo.atcNodes ?? []).map((v) => v.name).join(', ') || EMPTY;
            const activeIngredients =
              Array.isArray(extractedInfo.ingredients) && extractedInfo.ingredients.length > 0
                ? extractedInfo.ingredients.map((v) => v.label).join(', ')
                : extractedInfo.isActiveIngredient
                ? headerText
                : EMPTY;

            const isMedication = extractedInfo.selectedEventType === 'MEDICATION';
            return (
              <div
                key={`${extractedInfo.id}-${extractedInfo.selectedEventType}`}
                css={muiStyles.extractedInfo}
                style={{
                  borderLeftColor: colors.highlight,
                  background: colors.background,
                  cursor: 'pointer',
                }}
                data-name='extracted-info'
                data-id={extractedInfo.id}
                onClick={extractedInfoOnClick(extractedInfo.id)}
              >
                <div css={isMedication ? muiStyles.extractedInfoHeader : muiStyles.extractedInfoHeaderOnly}>
                  <Typography variant='labelBold' css={muiStyles.extractedInfoLabel}>
                    {headerText}
                  </Typography>
                </div>
                {isMedication && (
                  <>
                    <div css={muiStyles.extractedInfoRow}>
                      <Typography variant='labelBold' css={muiStyles.extractedInfoLabel}>
                        {t('resources.document_annotation.atcgroup')}
                      </Typography>
                      <Typography variant='value' css={muiStyles.extractedInfoValue}>
                        {atcGroups}
                      </Typography>
                    </div>
                    <div css={muiStyles.extractedInfoRow}>
                      <Typography variant='labelBold' css={muiStyles.extractedInfoLabel}>
                        {t('resources.document_annotation.active_ingredient')}
                      </Typography>
                      <Typography variant='value' css={muiStyles.extractedInfoValue}>
                        {activeIngredients}
                      </Typography>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <div data-name='legend' css={muiStyles.legend}>
          <IconHeaderWithSearch label={t('document.concepts.legend')} />
          <div css={muiStyles.legendInputs}>
            {legendTypes.map((v) => {
              const colors = getDocumentColors(v);
              const checked = !disabledEventTypes.includes(v);
              if (!checked) colors.background = 'var(--dlThemeGray-300)';
              return (
                <FormControlLabel
                  data-name='legend-filter'
                  data-checked={checked}
                  key={v}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleDisabledEventType(v)}
                      sx={{
                        'padding': 0,
                        // color: colors.highlight,
                        '&.Mui-checked': {
                          color: colors.highlight,
                        },
                      }}
                    />
                  }
                  label={tEnum('HaidiDocumentEventTypeEnum', v)}
                  css={muiStyles.legendInput}
                  style={{background: colors.background}}
                />
              );
            })}
          </div>
        </div>
      </LightComponentBox>
    </LightSplitLayout>
  );
};
const getStyles: GetStylesFunction = (options) => {
  return {
    documentColumn: css`
      display: flex;
      padding: 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    `,
    document: css`
      width: 100%;
      flex: 1;
      overflow-y: auto;
      padding: 40px;
      white-space: pre-wrap;
    `,
    infoLegendColumn: css`
      display: flex;
      padding: 16px;
      flex-direction: column;
      align-items: flex-start;
    `,
    extractedInfoColumn: css`
      margin-top: 12px;
      width: 100%;
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    `,
    extractedInfo: css`
      width: 100%;
      border-left: 4px solid black;
      padding: 12px 16px 12px 12px;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    `,
    extractedInfoHeaderOnly: css``,
    extractedInfoHeader: css`
      padding-bottom: 4px;
    `,
    extractedInfoRow: css`
      padding: 2px 0;
      display: flex;
      gap: 8px;
      justify-content: space-between;
      align-items: flex-start;
    `,
    extractedInfoLabel: css`
      color: var(--dlTheme-700);
      flex: 0.45 0 0;
    `,
    extractedInfoValue: css`
      color: var(--dlThemeGray-500);
      text-align: right;
      flex: 0.55 0 0;
    `,
    legend: css`
      width: 100%;
      height: 166px;
      padding: 16px 24px 24px 24px;
      border-radius: 8px;
      background: var(--dlThemeGray-100);
      display: flex;
      flex-direction: column;
    `,
    legendInputs: css`
      flex: 1;
      overflow-y: auto;
      margin-top: 16px;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      flex-wrap: wrap;
    `,
    legendInput: css`
      border-radius: 5px;
      padding: 7px 8px;
      font-size: 10px !important;
      line-height: 10px !important;
    `,
  };
};
